export type PhotoCoordinates = {
  latitude: number;
  longitude: number;
};

function finite(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value.trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function dms(value: unknown): number | null {
  if (Array.isArray(value) && value.length >= 3) {
    const degrees = finite(value[0]);
    const minutes = finite(value[1]);
    const seconds = finite(value[2]);
    if (degrees === null || minutes === null || seconds === null) return null;
    return degrees + minutes / 60 + seconds / 3600;
  }

  const direct = finite(value);
  if (direct !== null) return direct;

  // Some Android EXIF readers expose `40/1, 59/1, 123/100` instead of an
  // already-decimal value. Keep this parser local: the original EXIF object is
  // never uploaded, only the resulting coordinate pair is.
  if (typeof value !== 'string') return null;
  const parts = value.split(',').map((part) => {
    const [numerator, denominator = '1'] = part.trim().split('/');
    const top = Number(numerator);
    const bottom = Number(denominator);
    return Number.isFinite(top) && Number.isFinite(bottom) && bottom !== 0 ? top / bottom : NaN;
  });
  return parts.length >= 3 && parts.slice(0, 3).every(Number.isFinite)
    ? parts[0] + parts[1] / 60 + parts[2] / 3600
    : null;
}

function first(record: Record<string, unknown>, names: string[]): unknown {
  for (const name of names) {
    if (record[name] !== undefined && record[name] !== null) return record[name];
  }
  return undefined;
}

export function coordinatesFromExif(exif: Record<string, unknown> | null | undefined): PhotoCoordinates | null {
  if (!exif) return null;

  const nested = exif.GPS && typeof exif.GPS === 'object'
    ? exif.GPS as Record<string, unknown>
    : {};
  const latitude = dms(first(exif, ['GPSLatitude', 'Latitude', 'latitude']) ?? first(nested, ['GPSLatitude', 'Latitude', 'latitude']));
  const longitude = dms(first(exif, ['GPSLongitude', 'Longitude', 'longitude']) ?? first(nested, ['GPSLongitude', 'Longitude', 'longitude']));
  if (latitude === null || longitude === null) return null;

  const latitudeRef = String(first(exif, ['GPSLatitudeRef', 'LatitudeRef']) ?? first(nested, ['GPSLatitudeRef', 'LatitudeRef']) ?? '').toUpperCase();
  const longitudeRef = String(first(exif, ['GPSLongitudeRef', 'LongitudeRef']) ?? first(nested, ['GPSLongitudeRef', 'LongitudeRef']) ?? '').toUpperCase();
  const signedLatitude = latitudeRef === 'S' ? -Math.abs(latitude) : latitude;
  const signedLongitude = longitudeRef === 'W' ? -Math.abs(longitude) : longitude;

  if (signedLatitude < -90 || signedLatitude > 90 || signedLongitude < -180 || signedLongitude > 180) return null;
  // Limit transient precision before transport. The server never persists the
  // pair; it stores only the district/city returned by reverse geocoding.
  return {
    latitude: Number(signedLatitude.toFixed(6)),
    longitude: Number(signedLongitude.toFixed(6)),
  };
}
